import {
  DemandPartnerCpmMetric,
  DemandPartnerGrossNet,
  type DemandPartner,
  type DemandPartnerMutationPayload,
} from './model';
import { emptyDemandPartnerFormValue, type DemandPartnerFormValue } from './formSchema';

export interface DemandPartnerPayloadOptions {
  originalOwnerId?: string | null | undefined;
}

export function demandPartnerToFormValue(partner: DemandPartner): DemandPartnerFormValue {
  return {
    ...emptyDemandPartnerFormValue,
    id: partner.id,
    displayName: partner.displayName ?? partner.name ?? '',
    ownerId: partner.ownerId ?? partner.owner?.id ?? '',
    tcfVendorId: partner.tcfVendorId ?? '',
    bidderCode: partner.bidderCode ?? '',
    address: partner.address ?? '',
    status: partner.status,
    approvalsRequired: partner.approvalsRequired ?? false,
    urlCleanup: partner.urlCleanup ?? false,
    skipRequestsWithoutContentUrl: partner.skipRequestsWithoutContentUrl ?? false,
    nullifyContentChannelNetwork: partner.nullifyContentChannelNetwork ?? false,
    contacts: partner.contacts.map((contact) => ({
      id: contact.id,
      name: contact.name ?? '',
      role: contact.role ?? '',
      email: contact.email ?? '',
      phone: contact.phone ?? '',
    })),
    pricingType: partner.pricingType ?? null,
    revShareValue: partner.revShareValue ?? undefined,
    revShareGrossNet: partner.revShareGrossNet ?? DemandPartnerGrossNet.Net,
    ioCpmCostValue: partner.ioCpmCostValue ?? undefined,
    ioCpmCostMetric: partner.ioCpmCostMetric ?? DemandPartnerCpmMetric.AdRequest,
    additionalCost: partner.additionalCost ?? undefined,
    useStickyPricing: partner.useStickyPricing ?? false,
    biddingGrossNet: partner.biddingGrossNet ?? DemandPartnerGrossNet.Gross,
    biddingTmax: partner.biddingTmax ?? undefined,
    biddingBidTTL: partner.biddingBidTTL ?? undefined,
    revenueCapEnabled: partner.revenueCapEnabled ?? false,
    revenueCap: partner.revenueCap ?? undefined,
    includeUserIdInCacheKey: partner.includeUserIdInCacheKey ?? false,
    userSyncRedirectEnabled: Boolean(partner.userSyncRedirect),
    userSyncRedirect: partner.userSyncRedirect ?? '',
    userSyncIframeEnabled: Boolean(partner.userSyncIframe),
    userSyncIframe: partner.userSyncIframe ?? '',
    qps: partner.qps ?? null,
    cacheParamsVisible: false,
    cacheParamsEnabled: partner.cacheParamsEnabled ?? false,
    cacheUa: partner.cacheUa ?? false,
    cacheIp: partner.cacheIp ?? false,
    cacheBundle: partner.cacheBundle ?? false,
    cacheCountry: partner.cacheCountry ?? false,
    cacheCity: partner.cacheCity ?? false,
    includeBundles: partner.includeBundles !== false,
    includeDomains: partner.includeDomains !== false,
  };
}

export function demandPartnerToPayload(
  value: DemandPartnerFormValue,
  options: DemandPartnerPayloadOptions = {},
): DemandPartnerMutationPayload {
  const ownerId = emptyToUndefined(value.ownerId);
  const payload: DemandPartnerMutationPayload = {
    displayName: value.displayName.trim(),
    ...(ownerId ? { ownerId } : {}),
    tcfVendorId: value.tcfVendorId.trim(),
    bidderCode: emptyToNull(value.bidderCode),
    address: emptyToNull(value.address),
    status: value.status,
    approvalsRequired: value.approvalsRequired,
    contacts: value.contacts.map((contact) => ({
      ...(contact.id ? { id: contact.id } : {}),
      name: contact.name.trim(),
      role: emptyToNull(contact.role),
      email: emptyToNull(contact.email),
      phone: emptyToNull(contact.phone),
    })),
    pricingType: value.pricingType,
    revShareValue: finiteOrNull(value.revShareValue),
    revShareGrossNet: value.revShareGrossNet,
    ioCpmCostValue: finiteOrNull(value.ioCpmCostValue),
    ioCpmCostMetric: value.pricingType ? value.ioCpmCostMetric : null,
    additionalCost: finiteOrNull(value.additionalCost),
    useStickyPricing: value.useStickyPricing,
    biddingGrossNet: value.biddingGrossNet,
    biddingTmax: finiteOrNull(value.biddingTmax),
    biddingBidTTL: finiteOrNull(value.biddingBidTTL),
    revenueCapEnabled: value.revenueCapEnabled,
    revenueCap: value.revenueCapEnabled ? finiteOrNull(value.revenueCap) : null,
    includeUserIdInCacheKey: value.includeUserIdInCacheKey,
    userSyncRedirect: value.userSyncRedirectEnabled ? emptyToNull(value.userSyncRedirect) : null,
    userSyncIframe: value.userSyncIframeEnabled ? emptyToNull(value.userSyncIframe) : null,
    qps: value.qps,
    urlCleanup: value.urlCleanup,
    skipRequestsWithoutContentUrl: value.skipRequestsWithoutContentUrl,
    nullifyContentChannelNetwork: value.nullifyContentChannelNetwork,
    cacheParamsEnabled: value.cacheParamsVisible && value.cacheParamsEnabled,
    cacheUa: value.cacheParamsVisible && value.cacheParamsEnabled && value.cacheUa,
    cacheIp: value.cacheParamsVisible && value.cacheParamsEnabled && value.cacheIp,
    cacheBundle: value.cacheParamsVisible && value.cacheParamsEnabled && value.cacheBundle,
    cacheCountry: value.cacheParamsVisible && value.cacheParamsEnabled && value.cacheCountry,
    cacheCity: value.cacheParamsVisible && value.cacheParamsEnabled && value.cacheCity,
    includeBundles: value.includeBundles,
    includeDomains: value.includeDomains,
  };

  if (options.originalOwnerId && !value.ownerId) {
    delete payload.ownerId;
    payload.clearOwner = true;
  }

  return payload;
}

function emptyToNull(value: string) {
  return value.trim() || null;
}

function emptyToUndefined(value: string) {
  return value.trim() || undefined;
}

function finiteOrNull(value: number | undefined) {
  return value !== undefined && Number.isFinite(value) ? value : null;
}
